import React from 'react';
import SchemaEditor from "../../editor/SchemaEditor";
import BaseProps from "../../../types/BaseProps";
import SomlService from "../../../services/soml-service";
import Loader from "../../loader/Loader";
import Button from "react-bootstrap/Button";
import ValidationMessages from "../../validation/ValidationMessages";
import Alert from "react-bootstrap/Alert";
import Notification from "../../../common/Notification";
import classNames from 'classnames';
import DownloadSchemaAction from "../actions/DownloadSchemaAction";
import {Schema, SchemaResponse} from "../../../types/Schema";
import {SchemaExistsConfirmation} from "../common/SchemaExistsConfirmation";
import {extractSchemaIdAsync} from "../../../utils/soml-utils";
import {resolveErrorModel} from "../../../utils/validation-utils";
import {
  ValidationDataModel,
  ValidationResultModel,
  ValidationType
} from "../../../services/models/validation-model";

import './SchemaPlayground.scss';

type SchemaPlaygroundProps = BaseProps & {
  schemaId?: string;
  schema?: string;
  // The schema which is passed might be just a blank template in which case some functionality is
  // not applicable.
  isTemplate?: boolean;
  onChange?: (schema: string, dirty: boolean) => void;
  canCreate?: (schemaId: string) => boolean;
  onSave?: (schemaId: string) => void;
  onUpdate?: (schemaId: string) => void;
  actionsDisabled?: boolean
  errors?: any;
};

type SchemaPlaygroundState = {
  schema: string;
  schemaId?: string;
  isRequestRunning: boolean;
  hasLoaded: boolean;
  schemaExists: boolean;
  isTemplate: boolean;
  errors?: string;
  warnings?: string;
  errorResponse?: any;
  canUpdate?: boolean;
  dataValidation?: ValidationResultModel;
};

class SchemaPlayground extends React.Component<SchemaPlaygroundProps, SchemaPlaygroundState> {

  constructor(props: SchemaPlaygroundProps) {
    super(props);
    this.state = {
      schema: this.props.schema || '',
      isRequestRunning: false,
      hasLoaded: this.props.isTemplate || false,
      schemaExists: this.props.isTemplate || false,
      isTemplate: this.props.isTemplate || false
    };
    this.bindHandlers();
  }

  componentDidMount() {
    if (!this.state.isTemplate) {
      this.init();
    }
    if (this.props.errors) {
      this.setErrorMessages(this.props.errors);
    }
  }

  componentDidUpdate(prevProps: SchemaPlaygroundProps) {
    if (!this.state.isTemplate && this.props.schemaId !== prevProps.schemaId) {
      this.init();
    }
    // The error can be passed from the parent according to how the playground
    // is integrated.
    if (this.props.errors !== prevProps.errors) {
      this.setErrorMessages(this.props.errors);
    }
  }

  init() {
    this.loadSchema()
      .then(() => this.loadDataValidationReport())
      .catch((err: any) => {
        console.log('Loading error', err);
      }).finally(() => {
      this.setState({
        hasLoaded: true
      });
    });
  }

  private loadSchema(): Promise<any> {
    if (this.props.schemaId) {
      const schemaId = this.props.schemaId;
      this.setState({
        schemaId
      });
      return SomlService.getSchema(schemaId).then((schema: Schema) => {
        this.setState({
          schema: schema.schema,
          schemaExists: true,
          canUpdate: schema.permissions.canWrite
        });
        return schema.id;
      });
    }
    this.setState({
      schema: this.props.schema || '',
      hasLoaded: true,
      schemaExists: true,
    });
    return Promise.resolve();
  }

  private loadDataValidationReport(): Promise<ValidationResultModel | undefined> {
    if (this.state.schemaId) {
      return SomlService.getValidationMessages(this.state.schemaId)
        .then((result: ValidationResultModel) => {
          result.type = ValidationType.DATA_VALIDATION;
          const hasData = !!(result.warnings?.length || result.errors?.length || result.info?.length);
          this.setState({
            dataValidation: hasData ? result : undefined
          });
          return result;
        });
    }
    return Promise.resolve(undefined);
  }

  private bindHandlers() {
    this.onSchemaChange = this.onSchemaChange.bind(this);
    this.validateSchema = this.validateSchema.bind(this);
    this.saveSchema = this.saveSchema.bind(this);
    this.updateSchema = this.updateSchema.bind(this);
  }

  private onSchemaChange(value: string) {
    this.setState({
      schema: value,
    });
    this.props.onChange && this.props.onChange(value, true);
  }

  private validateSchema() {
    this.disableElements();
    SomlService.validateSchema(this.state.schema)
      .then(response => this.afterValidate(response))
      .catch(err => this.cannotValidate(err))
      .finally(() => this.enableElements());
  }

  private updateSchema() {
    if (this.state.schemaId) {
      this.disableElements();
      SomlService
        .updateSchema(this.state.schemaId, this.state.schema)
        .then(response => this.afterUpdate(response))
        .catch(err => this.cannotUpdate(err))
        .finally(() => this.enableElements());
    }
  }

  private saveSchema() {
    this.disableElements();
    extractSchemaIdAsync(this.state.schema).then((schemaId: string) => {
      this.setState({
        schemaId
      });
      const canCreate = this.props.canCreate && this.props.canCreate(schemaId);
      if (canCreate) {
        return SomlService.saveSchema(this.state.schema);
      }
      return Promise.reject('Insufficient permissions for schema create operation!');
    }).then((response: SchemaResponse) => {
      this.props.onChange && this.props.onChange(this.state.schema, false);
      this.afterSuccessfulRequest(response,
        'Schema was saved successfully.',
        'Schema cannot be saved. Check for warnings!');
      this.props.onSave && this.props.onSave(response.schema?.id || '');
    }).catch(err => {
      const errors = err.response ? err.response.data : err;
      this.setErrorMessages(errors);
      this.setState({
        errorResponse: errors
      });
      Notification.error({message: 'Cannot save schema, check for errors.'});
    }).finally(() => this.enableElements());
  }

  private disableElements() {
    this.setState({isRequestRunning: true});
  }

  private enableElements() {
    this.setState({isRequestRunning: false});
  }

  private afterSuccessfulRequest(response: SchemaResponse, successMessage: string, warningMessage: string) {
    this.clearValidationMessages();

    // If the response contains an input field -> schema is OK and is returned
    // If not -> probably some validation warnings are present
    if (response.schema) {
      Notification.success({message: successMessage});
    } else {
      this.setWarningMessages(response.response);
      Notification.warning({message: warningMessage});
    }
  }

  private afterFailedRequest(response: any, errorMessage: string) {
    this.setErrorMessages(response);
    Notification.error({message: errorMessage});
  }

  private afterValidate(response: SchemaResponse) {
    this.afterSuccessfulRequest(response,
      'Schema was validated successfully.',
      'Schema was validated with warnings.');
  }

  private cannotValidate(err: any) {
    this.afterFailedRequest(err, 'Cannot validate schema, check for errors.');
  }

  private afterUpdate(response: SchemaResponse) {
    this.props.onChange && this.props.onChange(this.state.schema, false);
    this.afterSuccessfulRequest(response,
      'Schema was updated successfully.',
      'Schema was updated with warnings.');
    if (this.state.isTemplate) {
      this.props.onUpdate && this.props.onUpdate(this.state.schemaId || '');
    }
  }

  private cannotUpdate(err: any) {
    this.afterFailedRequest(err, 'Cannot update schema, check for errors.');
  }

  private setErrorMessages(err: object) {
    this.setState({
      errors: err && JSON.stringify(err, null, 2),
      warnings: undefined
    });
  }

  private setWarningMessages(wrn: object) {
    this.setState({
      warnings: JSON.stringify(wrn, null, 2)
    });
  }

  private clearValidationMessages() {
    this.setState({
      errors: undefined,
      warnings: undefined,
      errorResponse: undefined
    });
  }

  private hasErrorMessages() {
    return !!this.state.errors;
  }

  private hasWarningMessages() {
    return !!this.state.warnings;
  }

  confirmOverride = () => {
    // If override is confirmed, the schema have to be updated.
    this.updateSchema();
  };

  denyOverride = () => {
    // Override is denied so just clear the errors.
    this.clearValidationMessages();
  };

  addValidationResults(results: ValidationResultModel[], dataType: string, data: ValidationDataModel[], validationType: ValidationType.SCHEMA_VALIDATION) {
    results.push({
      [dataType]: data,
      type: validationType
    })
  }

  render() {
    if (!this.state.hasLoaded) {
      return <Loader size='lg' block={true}/>
    }

    if (this.state.schemaExists) {
      const hasErrors = this.hasErrorMessages();
      const hasWarnings = this.hasWarningMessages();
      const classes = classNames('SchemaPlayground', {
        hasErrors,
        hasWarnings,
        hasDataValidation: this.state.dataValidation
      });

      const validationResults: ValidationResultModel[] = []
      if (hasErrors) {
        this.addValidationResults(
          validationResults,
          'errors',
          resolveErrorModel(JSON.parse(this.state.errors || '')),
          ValidationType.SCHEMA_VALIDATION);
      }
      if (hasWarnings) {
        this.addValidationResults(
          validationResults,
          'warnings',
          resolveErrorModel(JSON.parse(this.state.warnings || '')),
          ValidationType.SCHEMA_VALIDATION);
      }
      if (this.state.dataValidation) {
        validationResults.push(this.state.dataValidation);
      }

      return (
        <div className={classes}>
          <PlaygroundControls schemaId={this.state.schemaId}
                              isRequestRunning={this.state.isRequestRunning}
                              actionsDisabled={this.props.actionsDisabled}
                              isTemplate={this.state.isTemplate}
                              saveSchema={this.saveSchema}
                              updateSchema={this.updateSchema}
                              updateForbidden={!this.state.canUpdate}
                              validateSchema={this.validateSchema}/>
          <SchemaEditor value={this.state.schema} onChange={this.onSchemaChange}
                        hasErrors={hasErrors} hasWarnings={hasWarnings}/>
          <ValidationMessages model={validationResults}/>
          <SchemaExistsConfirmation schemaId={this.state.schemaId || ''}
                                    error={this.state.errorResponse}
                                    onConfirm={this.confirmOverride}
                                    onCancel={this.denyOverride}/>
        </div>
      );
    }

    return (
      <Alert className="MissingSchemaAlert" variant='warning'>
        Schema with id={this.state.schemaId} does not exist
      </Alert>
    );
  }

}

type PlaygroundControlsProps = {
  schemaId?: string;
  visible?: boolean;
  isRequestRunning: boolean;
  actionsDisabled?: boolean;
  isTemplate: boolean;
  saveSchema: () => void;
  updateSchema: () => void;
  validateSchema: () => void;
  updateForbidden?: boolean
}

function PlaygroundControls(props: PlaygroundControlsProps) {
  const visible = props.visible !== undefined ? props.visible : true;
  const loader = props.isRequestRunning ? <Loader/> : false;
  const updateTitle = props.updateForbidden ? "Update schema is forbidden" : "Update schema";
  const saveTitle = "Save schema";
  const disabled = props.isRequestRunning || props.actionsDisabled;
  return (!visible ? null : <div className="SchemaPlaygroundButtons">
    <Button disabled={disabled}
            className="ValidateSchemaButton"
            onClick={props.validateSchema}
            title="Validate Schema">
      Validate Schema
    </Button>
    {
      !props.isTemplate &&
      <Button disabled={disabled || props.updateForbidden}
              className="UpdateSchemaButton"
              onClick={props.updateSchema}
              title={updateTitle}>
        Update
      </Button>
    }
    {
      props.isTemplate &&
      <Button disabled={disabled}
              className="SaveSchemaButton"
              onClick={props.saveSchema}
              title={saveTitle}>
        Save
      </Button>
    }
    {
      props.schemaId && !props.isTemplate &&
      <DownloadSchemaAction schemaId={props.schemaId} standardButton={true}/>
    }
    {loader}
  </div>);
}

export default SchemaPlayground;
